/**
 * @file        src/pages/emailSettings/EmailSettings.jsx
 * @description Account Management → Email Settings. One card per brand (from
 *              the tenant's brand registry) plus the tenant-wide "Default /
 *              Admin" (null-brand) row, each with an editable SMTP form. A
 *              live Test Connection must pass for the exact values currently
 *              entered before Save is enabled (unless nothing SMTP-relevant
 *              changed since the last saved/verified config) — credentials are
 *              never persisted blind. Save/Delete both re-authenticate with
 *              the admin's own login password via the shared
 *              <PasswordConfirmModal />. The raw/full SMTP password is never
 *              rendered anywhere here — only `smtp_pass_last4`.
 * @author      Kinetika Labs
 * @created     2026-08-05
 * @changelog
 *   - 2026-10-09 Claude — Each saved brand is re-tested against the live SMTP
 *                          server when the screen opens (and on "Re-check"), and
 *                          the pill shows that result. It used to show the status
 *                          from the last save, so a mailbox password changed at the
 *                          provider still read "Connected" while every send failed.
 *   - 2026-08-08 Claude — SMTP port is now a preset <select> (465 SSL/TLS,
 *                          587 STARTTLS, 25 none, Custom) instead of a free
 *                          number input decoupled from the TLS checkbox —
 *                          port and encryption mode aren't independent
 *                          settings and presenting them as two unrelated
 *                          controls is what caused Test Connection to send
 *                          implicit TLS on port 587. Test error display now
 *                          shows the backend's plain-language message with a
 *                          "Technical details" toggle for the raw error.
 *   - 2026-08-05 Claude — Initial (per-brand SMTP Email Settings admin UI).
 *   - 2026-09-17 Claude — The read-only summary is now gated on the row still
 *                          being there, not only on `editing`. Saving a
 *                          not-yet-configured brand turned editing off before
 *                          the refetched list arrived, so the summary rendered
 *                          against a null row and crashed the page to blank
 *                          until a manual refresh.
 *   - 2026-08-10 Claude — SMTP port preset now uses the themed Select
 *                          (console-wide dropdown consistency pass) instead of
 *                          a native <select>.
 *   - 2026-09-17 Claude — SMTP password is now <PasswordInput>. It is pasted
 *                          from a provider and only validated by Test
 *                          Connection, so a silent typo cost a round trip.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import TableSkeleton from '../../components/TableSkeleton';
import PasswordConfirmModal from '../../components/PasswordConfirmModal';
import Select from '../../components/Select';
import { useBrands } from '../../api/brands';
import { qk } from '../../api/queryKeys';
import { listEmailSettings, testEmailSettings, verifyEmailSettings, saveEmailSettings, deleteEmailSettings, unwrapList } from '../../api/emailSettings';
import { toast } from '../../lib/toast';
import PasswordInput from '../../components/PasswordInput';

function extractError(e) {
  const err = e?.response?.data?.error;
  if (typeof err === 'string') return err;
  return err?.message || e?.response?.data?.message || 'Something went wrong. Please try again.';
}

const STATUS_LABEL = { connected: 'Connected', failed: 'Failed', unverified: 'Unverified' };
const STATUS_DOT = { connected: 'live', failed: 'failed', unverified: 'unverified' };

export function StatusPill({ status }) {
  const s = status || 'unverified';
  return (
    <span className="status-pill">
      <span className={`dot ${STATUS_DOT[s] || 'unverified'}`} />
      {STATUS_LABEL[s] || 'Unverified'}
    </span>
  );
}

export default function EmailSettings() {
  const qc = useQueryClient();
  const { options: brandOptions, isLoading: brandsLoading } = useBrands();

  const { data: raw, isLoading } = useQuery({
    queryKey: qk.emailSettings.list,
    queryFn: listEmailSettings,
  });
  const rows = unwrapList(raw);

  const slots = [
    { key: 'default', label: 'Default / Admin' },
    ...brandOptions.map((b) => ({ key: b.value, label: b.label })),
  ];

  const rowFor = (slotKey) => rows.find((r) => (r.brand === null || r.brand === 'default' ? 'default' : r.brand) === slotKey) || null;

  return (
    <>
      <PageHead
        title="Email Settings"
        subtitle="Account Management · per-brand SMTP delivery — verified before it's saved"
      />

      {isLoading || brandsLoading ? (
        <article className="panel glass"><TableSkeleton /></article>
      ) : (
        <div className="es-grid">
          {slots.map((slot) => (
            <EmailSettingsCard
              key={slot.key}
              slot={slot}
              row={rowFor(slot.key)}
              onChanged={() => qc.invalidateQueries({ queryKey: qk.emailSettings.list })}
            />
          ))}
        </div>
      )}
    </>
  );
}

// Port and encryption mode aren't independent settings — each preset carries
// the encryption mode a real mail server expects on that port. "Custom"
// leaves smtpSecure as a free on/off (STARTTLS toggle) for a non-standard port.
const PORT_PRESETS = [
  { value: '465', label: '465 (SSL/TLS)', secure: true },
  { value: '587', label: '587 (STARTTLS)', secure: true },
  { value: '25', label: '25 (None)', secure: false },
];
const PORT_PRESET_OPTIONS = [...PORT_PRESETS, { value: 'custom', label: 'Custom' }];

/** Which preset a stored port matches, so an already-saved config with a non-standard port still renders (as "Custom"). */
function presetForPort(port) {
  const p = String(port ?? '');
  return PORT_PRESETS.some((preset) => preset.value === p) ? p : 'custom';
}

function initialForm(row) {
  const port = row?.smtp_port != null ? String(row.smtp_port) : '587';
  return {
    fromName: row?.from_name || '',
    fromEmail: row?.from_email || '',
    replyTo: row?.reply_to || '',
    smtpHost: row?.smtp_host || '',
    smtpPort: port,
    portPreset: presetForPort(port),
    smtpSecure: row ? !!row.smtp_secure : true,
    smtpUser: row?.smtp_user || '',
    smtpPass: '',
  };
}

function EmailSettingsCard({ slot, row, onChanged }) {
  const hasRow = !!row;
  const [editing, setEditing] = useState(!hasRow);
  const [form, setForm] = useState(() => initialForm(row));
  const [testState, setTestState] = useState({ status: 'idle', message: '' }); // idle|testing|ok|error
  const [testedSignature, setTestedSignature] = useState(null);
  const [showTechnical, setShowTechnical] = useState(false);
  const [modal, setModal] = useState(null); // 'save' | 'delete' | null

  // Re-test the SAVED credentials whenever this card opens with a saved row, so
  // the pill reports whether the brand can send NOW, not when it was last saved.
  // The server records the result on the row; the list and the topbar status
  // are refetched from it.
  const qc = useQueryClient();
  const verifyMut = useMutation({
    mutationFn: () => verifyEmailSettings(slot.key),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.emailSettings.list });
      qc.invalidateQueries({ queryKey: qk.emailSettings.status });
    },
  });
  useEffect(() => {
    if (hasRow) verifyMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRow, slot.key]);

  // A different row landed (e.g. after save/delete invalidates the list) — reset
  // the card back to a clean view/edit state built from the fresh data.
  useEffect(() => {
    setForm(initialForm(row));
    setEditing(!row);
    setTestState({ status: 'idle', message: '' });
    setTestedSignature(null);
    setShowTechnical(false);
  }, [row]);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    // Any edit invalidates a prior passed test — it was for different values.
    setTestState({ status: 'idle', message: '' });
    setShowTechnical(false);
  };

  const setPortPreset = (preset) => {
    const matched = PORT_PRESETS.find((p) => p.value === preset);
    setForm((f) => ({
      ...f,
      portPreset: preset,
      ...(matched ? { smtpPort: matched.value, smtpSecure: matched.secure } : {}),
    }));
    setTestState({ status: 'idle', message: '' });
    setShowTechnical(false);
  };

  const signature = JSON.stringify({
    host: form.smtpHost.trim(), port: form.smtpPort, secure: !!form.smtpSecure,
    user: form.smtpUser.trim(), pass: form.smtpPass, fromEmail: form.fromEmail.trim(),
  });
  // Blank password = "keep the stored one", so the saved baseline signature always
  // carries an empty pass slot — this is what lets an admin edit only from_name /
  // reply_to without being forced to re-enter (and re-test) the SMTP password.
  const baselineSignature = hasRow ? JSON.stringify({
    host: row.smtp_host, port: String(row.smtp_port), secure: !!row.smtp_secure,
    user: row.smtp_user, pass: '', fromEmail: row.from_email,
  }) : null;

  const testPassedForCurrent = testState.status === 'ok' && testedSignature === signature;
  const unchangedFromSaved = hasRow && signature === baselineSignature;
  const canSave = testPassedForCurrent || unchangedFromSaved;
  const canTest = !!(form.smtpHost.trim() && form.smtpPort && form.smtpUser.trim() && form.smtpPass.trim() && form.fromEmail.trim());

  const testMut = useMutation({
    mutationFn: () => testEmailSettings(slot.key, {
      smtp_host: form.smtpHost.trim(),
      smtp_port: Number(form.smtpPort),
      smtp_secure: !!form.smtpSecure,
      smtp_user: form.smtpUser.trim(),
      smtp_pass: form.smtpPass,
      from_email: form.fromEmail.trim(),
    }),
    onSuccess: (res) => {
      const body = res?.data ?? res;
      setShowTechnical(false);
      if (body?.ok) {
        setTestState({ status: 'ok', message: 'Connection verified.' });
        setTestedSignature(signature);
      } else {
        setTestState({ status: 'error', message: body?.error || 'Connection failed.', technicalDetail: body?.technicalDetail || null });
      }
    },
    onError: (e) => {
      setShowTechnical(false);
      setTestState({ status: 'error', message: extractError(e), technicalDetail: null });
    },
  });

  const saveMut = useMutation({
    mutationFn: (password) => saveEmailSettings(slot.key, {
      smtp_host: form.smtpHost.trim(),
      smtp_port: Number(form.smtpPort),
      smtp_secure: !!form.smtpSecure,
      smtp_user: form.smtpUser.trim(),
      ...(form.smtpPass.trim() ? { smtp_pass: form.smtpPass } : {}),
      from_name: form.fromName.trim(),
      from_email: form.fromEmail.trim(),
      reply_to: form.replyTo.trim(),
      password,
    }),
  });

  const deleteMut = useMutation({
    mutationFn: (password) => deleteEmailSettings(slot.key, { password }),
  });

  async function confirmSave(password) {
    await saveMut.mutateAsync(password);
    // Wait for the refreshed list before closing, so the card flips straight to
    // the saved summary instead of flashing the edit form back at an admin who
    // has just been told it saved. A refetch failure must not be reported as a
    // failed save — the write already succeeded.
    await Promise.resolve(onChanged()).catch(() => {});
    toast.ok('Saved', `${slot.label} email settings saved.`);
    setModal(null);
    setEditing(false);
  }

  async function confirmDelete(password) {
    await deleteMut.mutateAsync(password);
    toast.ok('Removed', `${slot.label} email settings deleted.`);
    setModal(null);
    onChanged();
  }

  return (
    <article className="panel glass es-card">
      <div className="panel-head">
        <div>
          <h2>{slot.label}</h2>
          {hasRow
            ? (verifyMut.isPending
              ? <span className="status-pill"><span className="dot unverified" />Checking…</span>
              : <StatusPill status={row.status} />)
            : <Chip tone="pend">Not configured</Chip>}
        </div>
        {hasRow && !editing ? (
          <div className="head-btns">
            <button type="button" className="btn ghost sm" disabled={verifyMut.isPending} onClick={() => verifyMut.mutate()}>{verifyMut.isPending ? 'Checking…' : 'Re-check'}</button>
            <button type="button" className="btn ghost sm" onClick={() => setEditing(true)}><Icon name="edit" /> Edit</button>
            <button type="button" className="btn ghost sm" onClick={() => setModal('delete')}><Icon name="trash" /> Delete</button>
          </div>
        ) : null}
      </div>

      {/* `row` is re-checked here, not just `editing`: a first-time save flips
          editing off while the invalidated list query is still refetching, so
          for one render there is no row to summarise. Reading row.* there threw
          and (there being no error boundary) blanked the whole console. */}
      {!editing && row ? (
        <div className="es-summary">
          <div><small className="muted">From</small><div>{row.from_name} &lt;{row.from_email}&gt;</div></div>
          <div><small className="muted">Reply-to</small><div>{row.reply_to || <span className="muted">—</span>}</div></div>
          <div><small className="muted">SMTP host</small><div className="mono">{row.smtp_host}:{row.smtp_port}{row.smtp_secure ? ' (TLS)' : ''}</div></div>
          <div><small className="muted">SMTP user</small><div className="mono">{row.smtp_user}</div></div>
          <div><small className="muted">Password</small><div className="mono">•••• {row.smtp_pass_last4}</div></div>
          <div><small className="muted">Last verified</small><div>{row.last_verified_at ? new Date(row.last_verified_at).toLocaleString('en-IN') : <span className="muted">Never</span>}</div></div>
          {row.last_error ? <div className="field-error full">{row.last_error}</div> : null}
        </div>
      ) : (
        <form className="form-grid" onSubmit={(e) => { e.preventDefault(); if (canSave) setModal('save'); }}>
          <div className="field">
            <label>From name</label>
            <input value={form.fromName} onChange={set('fromName')} placeholder="NeonHolidays" />
          </div>
          <div className="field">
            <label>From email <span className="req">*</span></label>
            <input type="email" value={form.fromEmail} onChange={set('fromEmail')} placeholder="noreply@neonholidays.com" />
          </div>
          <div className="field">
            <label>Reply-to</label>
            <input type="email" value={form.replyTo} onChange={set('replyTo')} placeholder="support@neonholidays.com" />
          </div>
          <div className="field">
            <label>SMTP host <span className="req">*</span></label>
            <input value={form.smtpHost} onChange={set('smtpHost')} placeholder="smtp.example.com" />
          </div>
          <div className="field">
            <label>SMTP port <span className="req">*</span></label>
            <Select value={form.portPreset} onChange={setPortPreset} options={PORT_PRESET_OPTIONS} />
          </div>
          {form.portPreset === 'custom' ? (
            <div className="field">
              <label>Custom port <span className="req">*</span></label>
              <input type="number" min="1" max="65535" value={form.smtpPort} onChange={set('smtpPort')} placeholder="2525" />
            </div>
          ) : null}
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 22 }}>
            <input
              type="checkbox"
              checked={form.portPreset === '465' ? true : form.smtpSecure}
              disabled={form.portPreset === '465'}
              onChange={set('smtpSecure')}
              style={{ width: 'auto' }}
            />
            <span>Use TLS/SSL{form.portPreset === '465' ? ' (implicit on port 465)' : ''}</span>
          </label>
          <div className="field">
            <label>SMTP user <span className="req">*</span></label>
            <input value={form.smtpUser} onChange={set('smtpUser')} placeholder="apikey / username" />
          </div>
          <div className="field">
            <label>
              SMTP password{' '}
              {hasRow ? <span className="muted">(blank = keep current •••• {row.smtp_pass_last4})</span> : <span className="req">*</span>}
            </label>
            <PasswordInput
              value={form.smtpPass}
              onChange={set('smtpPass')}
              placeholder={hasRow ? 'Leave blank to keep existing' : 'SMTP password'}
              autoComplete="new-password"
            />
          </div>

          <div className="field full">
            <div className="es-test-row">
              <button type="button" className="btn ghost sm" disabled={!canTest || testMut.isPending} onClick={() => testMut.mutate()}>
                {testMut.isPending ? 'Testing…' : 'Test Connection'}
              </button>
              {testState.status === 'ok' ? <span className="es-test-result ok"><Icon name="check" /> {testState.message}</span> : null}
              {testState.status === 'error' ? (
                <div className="es-test-result-wrap">
                  <span className="es-test-result error"><Icon name="x" /> {testState.message}</span>
                  {testState.technicalDetail ? (
                    <button type="button" className="link-btn" onClick={() => setShowTechnical((s) => !s)}>
                      {showTechnical ? 'Hide' : 'Show'} technical details
                    </button>
                  ) : null}
                  {showTechnical && testState.technicalDetail ? (
                    <div className="mono es-test-technical">{testState.technicalDetail}</div>
                  ) : null}
                </div>
              ) : null}
              {!canTest ? <span className="muted es-test-hint">Host, port, user, password and from email are all required to test the connection.</span> : null}
            </div>
          </div>

          {!canSave ? (
            <div className="field-error full">Run a successful Test Connection with these exact values before saving.</div>
          ) : null}

          <div className="form-actions full">
            {hasRow ? (
              <button
                type="button"
                className="btn ghost"
                onClick={() => { setForm(initialForm(row)); setEditing(false); setTestState({ status: 'idle', message: '' }); }}
              >
                Cancel
              </button>
            ) : null}
            <button type="submit" className="btn primary" disabled={!canSave}>Save</button>
          </div>
        </form>
      )}

      {modal === 'save' ? (
        <PasswordConfirmModal
          title={`Save ${slot.label} email settings`}
          description="Re-enter your admin password to confirm — the server re-verifies the SMTP connection before saving."
          confirmLabel="Save settings"
          onConfirm={confirmSave}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal === 'delete' ? (
        <PasswordConfirmModal
          title={`Remove ${slot.label} email settings`}
          description="This permanently deletes the stored SMTP configuration for this brand."
          confirmLabel="Delete"
          danger
          onConfirm={confirmDelete}
          onClose={() => setModal(null)}
        />
      ) : null}
    </article>
  );
}
